/**
 * Org overview tab — plan summary, member count, suspend/restore actions.
 */
import { useState } from 'react';
import { api } from '../../../api';
import ReasonField from '../../../components/ops_admin/ReasonField';

export default function OrgOverviewTab({ org, opsRole, onRefresh }) {
  const canMutate = ['admin', 'owner'].includes(opsRole);
  const isOwner = opsRole === 'owner';
  const [showSuspend, setShowSuspend] = useState(false);
  const [showTerminate, setShowTerminate] = useState(false);
  const [suspendForm, setSuspendForm] = useState({ suspension_mode: 'read_only', reason: '', cooling_off_days: '' });
  const [terminateForm, setTerminateForm] = useState({ reason: '', confirm_org_name: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [restoreReason, setRestoreReason] = useState('');

  async function handleSuspend() {
    setSaving(true); setError(null);
    try {
      await api.post(`/ops_admin/orgs/${org.id}/suspend`, {
        ...suspendForm,
        cooling_off_days: suspendForm.cooling_off_days ? parseInt(suspendForm.cooling_off_days) : null,
      });
      setShowSuspend(false);
      onRefresh();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  async function handleRestore() {
    setSaving(true); setError(null);
    try {
      await api.post(`/ops_admin/orgs/${org.id}/restore?reason=${encodeURIComponent(restoreReason)}`);
      setRestoreReason('');
      onRefresh();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  async function handleTerminate() {
    setSaving(true); setError(null);
    try {
      await api.post(`/ops_admin/orgs/${org.id}/terminate`, terminateForm);
      setShowTerminate(false);
      onRefresh();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="ops-tab">
      <SetupStatus org={org} />
      <div className="info-grid">
        <div className="info-item"><label>ID</label><span className="mono">{org.id}</span></div>
        <div className="info-item"><label>Name</label><span>{org.name}</span></div>
        <div className="info-item"><label>Slug</label><span>{org.slug}</span></div>
        <div className="info-item"><label>Type</label><span>{org.org_type}</span></div>
        <div className="info-item"><label>State</label><span className={`badge badge-${org.state}`}>{org.state}</span></div>
        <div className="info-item"><label>Members</label><span>{org.member_count}</span></div>
        <div className="info-item"><label>Created</label><span>{org.created_at ? new Date(org.created_at).toLocaleDateString() : '—'}</span></div>
        <div className="info-item"><label>First login</label><span>{org.first_login_at ? new Date(org.first_login_at).toLocaleDateString() : 'Not yet'}</span></div>
        <div className="info-item"><label>Stripe</label><span>{org.stripe_customer_id || 'Not connected'}</span></div>
        {org.state === 'suspended' && <>
          <div className="info-item"><label>Suspended</label><span>{org.suspended_at ? new Date(org.suspended_at).toLocaleString() : '—'}</span></div>
          <div className="info-item"><label>Mode</label><span>{org.suspension_mode}</span></div>
        </>}
      </div>

      {error && <p className="error-text">{error}</p>}

      <StripeSection org={org} canMutate={canMutate} />

      {canMutate && (
        <div className="ops-actions">
          {org.state === 'active' && (
            <button className="btn btn-warning" onClick={() => setShowSuspend(true)}>Suspend</button>
          )}
          {org.state === 'suspended' && (
            <div className="restore-form">
              <ReasonField value={restoreReason} onChange={setRestoreReason} placeholder="Reason for restoring..." />
              <button className="btn btn-primary" onClick={handleRestore} disabled={saving || !restoreReason.trim()}>
                {saving ? 'Restoring...' : 'Restore'}
              </button>
            </div>
          )}
          {isOwner && org.state !== 'purged' && (
            <button className="btn btn-danger" onClick={() => setShowTerminate(true)}>Terminate (irreversible)</button>
          )}
        </div>
      )}

      {showSuspend && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Suspend Organization</h3>
            <div className="form-group">
              <label className="form-label">Suspension Mode</label>
              <select className="form-input" value={suspendForm.suspension_mode}
                onChange={e => setSuspendForm(f => ({...f, suspension_mode: e.target.value}))}>
                <option value="read_only">Read-only (users can log in, view data)</option>
                <option value="no_access">No access (users see suspended screen)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Auto-purge after (days) — leave blank for manual only</label>
              <input type="number" className="form-input" value={suspendForm.cooling_off_days}
                onChange={e => setSuspendForm(f => ({...f, cooling_off_days: e.target.value}))} />
            </div>
            <ReasonField value={suspendForm.reason} onChange={r => setSuspendForm(f => ({...f, reason: r}))} />
            {error && <p className="error-text">{error}</p>}
            <div className="button-row">
              <button className="btn btn-warning" onClick={handleSuspend} disabled={saving || !suspendForm.reason.trim()}>
                {saving ? 'Suspending...' : 'Suspend'}
              </button>
              <button className="btn btn-ghost" onClick={() => setShowSuspend(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showTerminate && (
        <div className="modal-overlay">
          <div className="modal modal-danger">
            <h3>Terminate Organization — IRREVERSIBLE</h3>
            <p className="danger-warning">
              This will purge all org data. Type the org name to confirm.
            </p>
            <div className="form-group">
              <label className="form-label">Type: <strong>{org.name}</strong></label>
              <input className="form-input" value={terminateForm.confirm_org_name}
                onChange={e => setTerminateForm(f => ({...f, confirm_org_name: e.target.value}))} />
            </div>
            <ReasonField value={terminateForm.reason} onChange={r => setTerminateForm(f => ({...f, reason: r}))} />
            {error && <p className="error-text">{error}</p>}
            <div className="button-row">
              <button className="btn btn-danger" onClick={handleTerminate}
                disabled={saving || terminateForm.confirm_org_name !== org.name || !terminateForm.reason.trim()}>
                {saving ? 'Terminating...' : 'Permanently Terminate'}
              </button>
              <button className="btn btn-ghost" onClick={() => setShowTerminate(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SetupStatus({ org }) {
  const adminProvisioned = org.member_count > 0;
  const firstLogin = !!org.first_login_at;
  const postureSet = org.privacy_posture && org.privacy_posture !== 'max';
  const stripeConnected = !!org.stripe_customer_id;

  const items = [
    { done: adminProvisioned, label: 'Admin user provisioned', detail: org.member_count ? `${org.member_count} member${org.member_count === 1 ? '' : 's'}` : 'No members yet' },
    { done: firstLogin, label: 'Admin first login', detail: firstLogin ? new Date(org.first_login_at).toLocaleString() : 'Pending — share setup link' },
    { done: postureSet, label: 'Privacy posture chosen', detail: postureLabel(org.privacy_posture) },
    { done: stripeConnected, label: 'Stripe connected', detail: stripeConnected ? org.stripe_customer_id : 'Not connected (skip until billing day)' },
  ];

  const remaining = items.filter(i => !i.done).length;

  return (
    <div className="setup-status-panel">
      <div className="setup-status-header">
        <h3>Setup Status</h3>
        <span className="setup-status-summary">
          {remaining === 0 ? 'All set' : `${items.length - remaining} of ${items.length} complete`}
        </span>
      </div>
      <ul className="setup-status-list">
        {items.map(item => (
          <li key={item.label} className={item.done ? 'done' : 'pending'}>
            <span className="setup-status-icon">{item.done ? '✓' : '○'}</span>
            <span className="setup-status-label">{item.label}</span>
            <span className="setup-status-detail">{item.detail}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StripeSection({ org, canMutate }) {
  const connected = !!org.stripe_customer_id;
  const customerUrl = connected
    ? `https://dashboard.stripe.com/customers/${org.stripe_customer_id}`
    : null;

  return (
    <div className="stripe-section">
      <div className="stripe-section-header">
        <h3>Stripe</h3>
        <span className={`badge ${connected ? 'badge-active' : 'badge-pending'}`}>
          {connected ? 'Connected' : 'Not connected'}
        </span>
      </div>
      {connected ? (
        <div className="stripe-connected-row">
          <span className="mono">{org.stripe_customer_id}</span>
          <a href={customerUrl} target="_blank" rel="noopener" className="btn btn-sm btn-ghost">
            Open in Stripe ↗
          </a>
        </div>
      ) : (
        <div className="stripe-disconnected-row">
          <p className="setup-status-detail">
            Billing activation is deferred until the day before this customer pays.
            When you're ready, click below to create a Stripe customer and link it.
          </p>
          <button
            className="btn btn-sm btn-secondary"
            disabled={!canMutate}
            title="Coming with billing activation — Stripe wiring is implemented but not enabled yet"
            onClick={() => alert('Stripe activation is deferred per the OPS_ADMIN_PLAN. Wire up LiveStripeClient before enabling.')}
          >
            Connect Stripe customer
          </button>
        </div>
      )}
    </div>
  );
}

function postureLabel(p) {
  switch (p) {
    case 'max_cj': return 'Max + Criminal Justice';
    case 'max': return 'Max (default — strictest)';
    case 'names': return 'Names visible';
    case 'full': return 'Full support access';
    default: return p || 'unknown';
  }
}
